import { initializeApp } from 'firebase/app';
import {
  AuthError,
  AuthErrorCodes,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  getAuth,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import React, { createContext } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { toast } from 'react-toastify';
import { UserAuthState, useUserAuthStore } from 'src/store/userAuth/userAuthStore';
import { AuthContextInterface } from '../../context/AuthContextInterface';
import { useSessionMutation } from 'src/services/restApi/queries/authentication.service';
import firebaseConfig from './config/firebaseConfig';

const app = initializeApp(firebaseConfig.config);

export const FirebaseAuthContext = createContext<AuthContextInterface>({
  user: null,
  loading: false,
  error: undefined,
  isAuthenticated: false,
  getTokens: () => new Promise((resolve) => null),
  loginWithEmailAndPassword: (email: string, password: string) => new Promise((resolve) => {}),
  loginWithGoogle: () => new Promise((resolve) => {}),
  register: (email: string, password: string) => new Promise((resolve) => {}),
  logout: () => new Promise((resolve) => {}),
  refreshToken: () => new Promise((resolve) => {}),
  verifyEmail: () => new Promise((resolve) => {}),
  resetPassword: (email: string) => new Promise((resolve) => {}),
  emailVerified: false,
  authHook: undefined,
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export const FirebaseAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = getAuth(app);
  const [user, loading, error] = useAuthState(auth);
  const setUserState = useUserAuthStore((state) => state.setUserState);
  const [recordSession] = useSessionMutation();

  const loginWithEmailAndPassword = async (email: string, password: string) => {
    try {
      const firebaseUser = await signInWithEmailAndPassword(auth, email, password);
      const user: UserAuthState = {
        user: firebaseUser,
        userStatus: 'authenticated',
        accessToken: await firebaseUser.user.getIdToken(),
        refreshToken: firebaseUser.user.refreshToken,
        error: null,
      };
      setUserState(user);
      recordSession().catch(() => {/* non-blocking — login succeeds regardless */});
    } catch (error) {
      handleLoginError(error as AuthError);
    }
  };

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // session recording skipped — Google login not used
    } catch (error: any) {
      handleError(error);
    }
  };

  const register = async (email: string, password: string) => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      handleError(error);
    }
  };

  const logout = async () => {
    try {
      logoutFirebase();
    } catch (error: any) {
      handleError(error);
    }
  };

  const refreshToken = async () => {
    try {
      const res = await refreshFirebaseToken();
    } catch (error: any) {
      handleError(error);
    }
  };

  const getTokens = async () => {
    const currentUser = auth.currentUser;
    try {
      return {
        accessToken: (await currentUser?.getIdToken(true)) ?? null,
        refreshToken: currentUser?.refreshToken ?? null,
      };
    } catch (error: any) {
      handleError(error);
      return null;
    }
  };

  const verifyEmail = async () => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        await sendEmailVerification(currentUser);
      } catch (error: any) {
        handleError(error);
      }
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: any) {
      handleError(error);
    }
  };

  const handleLoginError = (error: AuthError) => {
    let errorMessage = 'Login failed. Please try again.';

    switch (error.code) {
      case AuthErrorCodes.USER_DELETED:
        errorMessage = 'No account found with this email address.';
        break;
      case AuthErrorCodes.INVALID_PASSWORD:
        errorMessage = 'Incorrect password. Please try again.';
        break;
      case AuthErrorCodes.INVALID_EMAIL:
        errorMessage = 'Invalid email address format.';
        break;
      case AuthErrorCodes.USER_DISABLED:
        errorMessage = 'This account has been disabled. Please contact support.';
        break;
      case AuthErrorCodes.TOO_MANY_ATTEMPTS_TRY_LATER:
        errorMessage = 'Too many failed login attempts. Please try again later.';
        break;
      case AuthErrorCodes.NETWORK_REQUEST_FAILED:
        errorMessage = 'Network error. Please check your connection and try again.';
        break;
      case AuthErrorCodes.INVALID_LOGIN_CREDENTIALS:
        errorMessage = 'Invalid email or password. Please check your credentials.';
        break;
      case AuthErrorCodes.WEAK_PASSWORD:
        errorMessage = 'Password is too weak. Please use a stronger password.';
        break;
      default:
        errorMessage = `Authentication failed: ${error.message}`;
        break;
    }

    toast.error(errorMessage);
    console.error('Login Error:', { code: error.code, message: error.message });
  };

  const handleError = (error: AuthError) => {
    toast.error('Error Authenticating...');
    console.log('Authentication Error:', error);
  };

  const emailVerified = true; //auth.currentUser ? auth.currentUser.emailVerified : false;

  const isAuthenticated = user ? true : false;

  const authHook = useAuthState;

  return (
    <FirebaseAuthContext.Provider
      value={{
        isAuthenticated,
        user,
        loading,
        error,
        getTokens,
        loginWithEmailAndPassword,
        loginWithGoogle,
        register,
        logout,
        refreshToken,
        verifyEmail,
        resetPassword,
        emailVerified,
        authHook,
      }}
    >
      {children}
    </FirebaseAuthContext.Provider>
  );
};

export const refreshFirebaseToken = async () => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Cannot get new token');
    }
    return {
      accessToken: await currentUser?.getIdToken(true),
      refreshToken: currentUser?.refreshToken,
    };
  } catch (error) {
    return {
      refreshToken: null,
      accessToken: null,
    };
  }
};

export const logoutFirebase = async () => {
  const auth = getAuth();
  await signOut(auth);
  const userAuthStore = useUserAuthStore.getState();
  if (userAuthStore && userAuthStore.resetUserState) {
    userAuthStore.resetUserState();
  }
  window.location.href = '/auth/login';
};
